import { streetOrientationToName } from "building-common/streetOrientation/streetOrientationTools";
import { getWallStreetOrientation } from "building-common/walls/wall";
import { getActiveProfileTable } from "building-generation/elements/profileTools";
import type { StreetOrientation } from "building-types/building/streetOrientationTypesEnum";
import type { SideWallSweeps, WallSweepSystem } from "building-types/facades/facadeTypes";
import type { PlanarGraphPerimeterInfo } from "building-types/planarGraphs/planarGraphPerimeterInfo";
import type { ProfileTable } from "building-types/profiles/profileTypes";
import { ProfileExtrusionDescriptorType } from "building-types/profiles/profileTypesEnum";
import type { WallSystem } from "building-types/walls/wallSytemTypes";
import type { WorldInches } from "common/types/nominalNumbersTypes";
import { worldInchesNew } from "common/utils/nominalNumbers";
import { polyLine3Length } from "geom/polyLine3";
import { queryProfileDescriptor } from "../queryProfileDescriptor";
import type { ExteriorTrimCollectionBuilding, TrimKeyMeasure } from "../trimTypes";
import type { TrimKeyMeasureType } from "../trimTypes";

export function getRoomFacadeTrims(
  building: ExteriorTrimCollectionBuilding,
  trimKeyMeasures: TrimKeyMeasure[] = [],
): TrimKeyMeasure[] {
  if (!building) {
    return trimKeyMeasures;
  }

  const { levels, walls, wallSweeps, planarGraphPerimeters } = building;
  const profileTable = getActiveProfileTable({
    settingsToCatalogs: building.enabledFeatures.settingsToCatalogs,
    profiles: building.doc.profiles,
    profileSettings: building.settings.profile,
  });

  for (const levelId in levels) {
    getWallSweeps(
      "ROOM_FACADE_TRIMS",
      wallSweeps[levelId],
      profileTable,
      walls[levelId],
      planarGraphPerimeters[levelId],
      building.enabledFeatures.settingsToCatalogs,
      trimKeyMeasures,
    );
  }

  return trimKeyMeasures;
}

export function getRoofFacadeTrims(
  building: ExteriorTrimCollectionBuilding,
  trimKeyMeasures: TrimKeyMeasure[] = [],
): TrimKeyMeasure[] {
  if (!building) {
    return trimKeyMeasures;
  }

  const { roofSystems, wallSweeps } = building;
  const profileTable = getActiveProfileTable({
    settingsToCatalogs: building.enabledFeatures.settingsToCatalogs,
    profiles: building.doc.profiles,
    profileSettings: building.settings.profile,
  });

  for (const roofSystemId in roofSystems) {
    const { roofs } = roofSystems[roofSystemId];

    for (const roofId in roofs) {
      const { roofWallSystem } = roofs[roofId];

      if (!roofWallSystem) {
        continue;
      }

      const { levelId, planarGraphPerimeterInfo, wallSystem } = roofWallSystem;
      getWallSweeps(
        "ROOF_FACADE_TRIMS",
        wallSweeps[levelId],
        profileTable,
        wallSystem,
        planarGraphPerimeterInfo,
        building.enabledFeatures.settingsToCatalogs,
        trimKeyMeasures,
      );
    }
  }

  return trimKeyMeasures;
}

export function getFacadeTrims(
  building: ExteriorTrimCollectionBuilding,
  trimKeyMeasures: TrimKeyMeasure[] = [],
): TrimKeyMeasure[] {
  if (!building) {
    return trimKeyMeasures;
  }

  getRoomFacadeTrims(building, trimKeyMeasures);
  getRoofFacadeTrims(building, trimKeyMeasures);

  return trimKeyMeasures;
}

function getWallSweeps(
  category: TrimKeyMeasureType,
  wallSweepSystem: WallSweepSystem,
  profileTable: ProfileTable,
  wallSystem: WallSystem | undefined,
  perimeterInfo: PlanarGraphPerimeterInfo | undefined,
  settingsToCatalogs: boolean | undefined,
  trimKeyMeasures: TrimKeyMeasure[] = [],
): TrimKeyMeasure[] {
  if (!wallSweepSystem) {
    return trimKeyMeasures;
  }

  const { sweeps } = wallSweepSystem;

  for (const wallId in sweeps) {
    const { leftWallSweeps, rightWallSweeps } = sweeps[wallId];
    const wall = wallSystem?.walls[wallId];
    const streetOrientation = wall ? getWallStreetOrientation(wall, perimeterInfo) : undefined;

    getWallSideSweeps(category, leftWallSweeps, profileTable, streetOrientation, settingsToCatalogs, trimKeyMeasures);
    getWallSideSweeps(category, rightWallSweeps, profileTable, streetOrientation, settingsToCatalogs, trimKeyMeasures);
  }

  return trimKeyMeasures;
}

function getWallSideSweeps(
  category: TrimKeyMeasureType,
  sideWallSweeps: SideWallSweeps,
  profileTable: ProfileTable,
  streetOrientation: StreetOrientation | undefined,
  settingsToCatalogs: boolean | undefined,
  trimKeyMeasures: TrimKeyMeasure[] = [],
): TrimKeyMeasure[] {
  const { rails } = sideWallSweeps;

  trimKeyMeasures.push(
    ...rails.flatMap((facadeTrimRail) => {
      const descriptor = queryProfileDescriptor(profileTable, facadeTrimRail.profileId, settingsToCatalogs);
      if (!descriptor) {
        return [];
      }
      let height: WorldInches | undefined;
      let width: WorldInches | undefined;
      if (descriptor.profileType === ProfileExtrusionDescriptorType.BOARD) {
        height = worldInchesNew(descriptor.height);
        width = worldInchesNew(descriptor.width);
      }

      return [
        {
          category,
          name: facadeTrimRail.profileName,
          profileId: facadeTrimRail.profileId,
          length: worldInchesNew(polyLine3Length(facadeTrimRail.rail)),
          height,
          width,
          streetOrientation: streetOrientationToName(streetOrientation),
          materialSlot: facadeTrimRail.material,
          rail: facadeTrimRail.rail,
        },
      ];
    }),
  );

  return trimKeyMeasures;
}
